/**
 * The Units settings form (next-phase plan, step 7): the units this plant reads its numbers in. A system (metric or
 * US) as the main choice, then one select per dimension (temperature, pressure, mass, length, volume, time) that
 * follows the system until it is told otherwise, and a live preview of four sample values in what the draft would
 * show. An org admin saves it (and may go back to the default); anyone else reads it. Storage never changes: this only
 * says what a stored value is shown in (lib/units).
 *
 * UnitsSettingsView is pure (rendered in tests without providers); UnitsSettings holds the read, the draft and the
 * save. It is exported for the Settings page to mount as a tab; this file adds no route.
 */
import { useId, useState } from "react";
import { Loader2, Ruler } from "lucide-react";
import { useBriefLive } from "@/lib/api/brief";
import { useDisplayUnits, useSaveDisplayUnits, type DisplayUnitsSource } from "@/lib/api/display-units";
import { useOrg } from "@/lib/org-context";
import { Button } from "@/lib/ui/button";
import { Card } from "@/lib/ui/card";
import { Label } from "@/lib/ui/label";
import { NativeSelect } from "@/lib/ui/native-select";
import { RadioCard, RadioCardGroup } from "@/lib/ui/radio-cards";
import { formatQuantity } from "@/lib/units/format";
import {
  DIMENSIONS,
  DIMENSION_NAMES,
  SYSTEM_NAMES,
  SYSTEM_UNITS,
  UNIT_OPTIONS,
  samePrefs,
  withOverride,
  withSystem,
  type DimensionKey,
  type DisplayPrefs,
  type UnitSystem,
} from "@/lib/units/prefs";
import {
  AS_RECORDED,
  FOLLOW_SYSTEM,
  LIVE_ONLY_NOTE,
  OTHER_SYSTEM_UNITS,
  PREVIEW_SAMPLES,
  READING_NOTE,
  READ_FAILED_NOTE,
  READ_ONLY_NOTE,
  SYSTEM_LINES,
  UNITS_LEAD,
  UNITS_TITLE,
  UNKNOWN_UNITS_NOTE,
  unitsErrorWords,
} from "@/lib/units/words";

const SYSTEMS: readonly UnitSystem[] = ["si", "us"];

/** What a system shows, in a line (SYSTEM_LINES). */
function systemLine(system: UnitSystem): string {
  return SYSTEM_LINES[system];
}

/** The select's first option: what the system shows the other system's units of a dimension in, or as recorded. */
function followLabel(system: UnitSystem, dim: DimensionKey): string {
  const unit = SYSTEM_UNITS[system][dim];
  return unit === undefined ? AS_RECORDED : `${FOLLOW_SYSTEM}: ${OTHER_SYSTEM_UNITS[system]} in ${unit}`;
}

export interface UnitsSettingsViewProps {
  /** The draft: what the form shows and the preview converts by. */
  prefs: DisplayPrefs;
  /** Where the saved choice comes from. */
  source: DisplayUnitsSource;
  /** An org admin may change it. */
  canEdit: boolean;
  /** The saved choice is still on its way, or could not be read: the form waits, and offers no save. */
  waiting?: boolean;
  /** The draft differs from what is saved. */
  dirty: boolean;
  saving: boolean;
  /** The last save went through. */
  saved: boolean;
  /** The last refusal or failure, in words. */
  error: string | null;
  onChange: (next: DisplayPrefs) => void;
  onSave: () => void;
  /** Clears the org's choice back to the default. */
  onUseDefault: () => void;
}

/** The form, pure: exported for the tests. */
export function UnitsSettingsView(p: UnitsSettingsViewProps) {
  const id = useId();
  const waiting = p.waiting === true;
  const locked = !p.canEdit || waiting;
  return (
    <Card data-units-settings={p.source} className="flex flex-col gap-5 p-4">
      <div className="flex flex-col gap-1">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-on-surface">
          <Ruler aria-hidden className="size-4 text-on-surface-muted" />
          {UNITS_TITLE}
        </h2>
        <p className="text-sm text-on-surface-muted">{UNITS_LEAD}</p>
      </div>

      <div className="flex flex-col gap-2">
        <span className="text-sm font-medium text-on-surface">System</span>
        <RadioCardGroup
          name={`${id}-system`}
          value={p.prefs.system}
          onValueChange={(v) => p.onChange(withSystem(p.prefs, v as UnitSystem))}
          disabled={locked}
          aria-label="Unit system"
          className="sm:flex-row"
        >
          {SYSTEMS.map((system) => (
            <RadioCard
              key={system}
              value={system}
              title={SYSTEM_NAMES[system]}
              description={systemLine(system)}
              className="sm:flex-1"
            />
          ))}
        </RadioCardGroup>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {DIMENSIONS.map((dim) => (
          <div key={dim} className="flex flex-col gap-1.5">
            <Label htmlFor={`${id}-${dim}`}>{DIMENSION_NAMES[dim]}</Label>
            <NativeSelect
              id={`${id}-${dim}`}
              value={p.prefs.overrides[dim] ?? ""}
              onChange={(e) => p.onChange(withOverride(p.prefs, dim, e.target.value))}
              disabled={locked}
            >
              <option value="">{followLabel(p.prefs.system, dim)}</option>
              {UNIT_OPTIONS[dim].map((unit) => (
                <option key={unit} value={unit}>
                  {unit}
                </option>
              ))}
            </NativeSelect>
          </div>
        ))}
      </div>

      <div className="flex flex-col gap-2">
        <h3 className="text-sm font-medium text-on-surface">Preview</h3>
        <ul data-units-preview className="grid gap-2 sm:grid-cols-2">
          {PREVIEW_SAMPLES.map((sample) => {
            const shown = formatQuantity(sample.value, sample.unit, p.prefs);
            return (
              <li key={sample.label} className="flex flex-col rounded-md border border-border bg-surface-muted/40 px-3 py-2">
                <span className="text-xs text-on-surface-muted">{sample.label}</span>
                <span className="flex flex-wrap items-baseline gap-x-2 text-sm text-on-surface">
                  <span className="text-on-surface-muted">{`${sample.value} ${sample.unit}`}</span>
                  <span aria-hidden className="text-on-surface-muted">
                    shown as
                  </span>
                  <span className="font-medium tabular-nums">{shown.text}</span>
                </span>
              </li>
            );
          })}
        </ul>
        <p className="text-xs text-on-surface-muted">{UNKNOWN_UNITS_NOTE}</p>
      </div>

      {waiting && !p.error && <p className="text-sm text-on-surface-muted">{READING_NOTE}</p>}
      {!p.canEdit && <p className="text-sm text-on-surface-muted">{READ_ONLY_NOTE}</p>}
      {p.error && (
        <p role="alert" className="text-sm text-danger">
          {p.error}
        </p>
      )}
      {p.canEdit && !waiting && (
        <div className="flex flex-wrap items-center gap-2">
          <Button type="button" onClick={p.onSave} disabled={!p.dirty || p.saving}>
            {p.saving && <Loader2 aria-hidden className="animate-spin" />}
            {p.saving ? "Saving" : "Save"}
          </Button>
          {p.source === "org" && (
            <Button type="button" variant="outline" onClick={p.onUseDefault} disabled={p.saving}>
              Use the default
            </Button>
          )}
          {p.saved && !p.dirty && (
            <span role="status" className="text-sm text-on-surface-muted">
              Saved
            </span>
          )}
        </div>
      )}
    </Card>
  );
}

/**
 * The form for the signed-in person's org: the saved choice (metric until it is read), an editable draft over it, and
 * the save. An org admin changes and saves it; a member reads it.
 */
export function UnitsSettings() {
  const live = useBriefLive();
  const org = useOrg();
  const units = useDisplayUnits();
  const save = useSaveDisplayUnits();
  const [draft, setDraft] = useState<DisplayPrefs | null>(null);
  const [saved, setSaved] = useState(false);
  const prefs = draft ?? units.prefs;
  const dirty = draft !== null && !samePrefs(draft, units.prefs);
  const finish = (next: DisplayPrefs | null) =>
    save.mutate(next, {
      onSuccess: () => {
        setDraft(null);
        setSaved(true);
      },
    });
  // Off the live platform there is no choice to read and nothing to save: a note, never a form with a Save to press.
  if (!live) {
    return (
      <Card className="flex flex-col gap-2 p-4">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-on-surface">
          <Ruler aria-hidden className="size-4 text-on-surface-muted" />
          {UNITS_TITLE}
        </h2>
        <p className="text-sm text-on-surface-muted">{LIVE_ONLY_NOTE}</p>
      </Card>
    );
  }
  return (
    <UnitsSettingsView
      prefs={prefs}
      source={units.source}
      canEdit={org.can("org.admin")}
      // Nothing is changed on top of a choice that is still on its way, or that could not be read (it shows metric).
      waiting={units.isLoading || units.isError}
      dirty={dirty}
      saving={save.isPending}
      saved={saved && save.isSuccess}
      error={units.isError ? READ_FAILED_NOTE : save.isError ? unitsErrorWords(save.error) : null}
      onChange={(next) => {
        setSaved(false);
        save.reset();
        setDraft(next);
      }}
      onSave={() => {
        if (draft) finish(draft);
      }}
      onUseDefault={() => finish(null)}
    />
  );
}
